import { useCallback, useState } from "react"

import { Typography, Button, Stack, TextField, Box, Chip } from "@mui/material"
import { ClearIcon } from "@mui/x-date-pickers"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import {
  LinkButtonHybrid,
  StyledHelperText,
  ResponsiveDrawer,
} from "@/components"

import { PassHolderFormData } from "../../schema"
import { useMemberCoupon, ValidatedCoupon } from "./hooks/useMemberCoupon"

type MemberCouponFormProps = {
  couponCode?: string
  onSuccess: (coupon: ValidatedCoupon) => void
  onResetCoupon: () => void
}

const MemberCouponField = ({
  couponCode,
  onSuccess,
  onResetCoupon,
}: MemberCouponFormProps) => {
  const { control: formControl } = useFormContext<PassHolderFormData>()
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onSubmit(e)
  }

  const openModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const onCouponSuccess = useCallback(
    (coupon: ValidatedCoupon) => {
      onSuccess(coupon)
      closeModal()
    },
    [onSuccess, closeModal]
  )
  const [firstName, lastName, birthDate] = useWatch({
    control: formControl,
    name: ["firstName", "lastName", "birthDate"],
  })

  const { onSubmit, control, errors, isValidating, reset } = useMemberCoupon({
    firstName,
    lastName,
    birthDate,
    onSuccess: onCouponSuccess,
  })

  const onFormClose = useCallback(() => {
    reset()
    closeModal()
  }, [reset, closeModal])

  const renderChip = () => {
    if (!couponCode) return null

    return (
      <Chip
        label={`Kampanjkod: ${couponCode}`}
        color="secondary"
        onDelete={onResetCoupon}
        deleteIcon={<ClearIcon />}
        sx={{ alignSelf: "flex-start" }}
      />
    )
  }

  const renderFormFooter = () => (
    <>
      <Button type="button" variant="outlined" onClick={onFormClose}>
        {t("passHolderDetails.form.cancelButtonLabel")}
      </Button>
      <Button
        form="formModal"
        type="submit"
        variant="contained"
        disabled={isValidating}
      >
        {isValidating
          ? t("passHolderDetails.form.validating")
          : t("passHolderDetails.form.submitButtonLabel")}
      </Button>
    </>
  )

  const renderFormContent = () => (
    <Stack spacing={2}>
      <Typography variant="body2">
        {t("passHolderDetails.form.memberCouponDescription")}
      </Typography>
      <Controller
        name="couponCode"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            autoFocus
            fullWidth
            label={t("passHolderDetails.form.memberCouponLabel")}
            placeholder={t("passHolderDetails.form.memberCouponPlaceholder")}
            error={!!errors.couponCode}
            helperText={
              error?.message ? (
                <StyledHelperText helperText={error.message} severity="error" />
              ) : undefined
            }
            onChange={(e) => field.onChange(e.target.value.toUpperCase())}
          />
        )}
      />
    </Stack>
  )

  return (
    <>
      <Box display="flex" flexDirection="column" alignSelf="flex-start" gap={1}>
        <LinkButtonHybrid onClick={openModal}>
          {t("passHolderDetails.form.memberCoupon")}
        </LinkButtonHybrid>
        {renderChip()}
      </Box>
      <ResponsiveDrawer
        open={isModalOpen}
        onClose={onFormClose}
        title={t("passHolderDetails.form.memberCouponTitle")}
        footer={renderFormFooter()}
      >
        <form onSubmit={handleSubmit} id="formModal">
          {renderFormContent()}
        </form>
      </ResponsiveDrawer>
    </>
  )
}

export default MemberCouponField
